import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Divider,
  IconButton,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  alpha,
} from "@mui/material";
import {
  Close,
  Save,
  Inventory,
  Add as AddIcon,
  Remove as RemoveIcon,
  WarningAmber,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { adjustMedicationStock } from "../../../../../../api/services/medication";
import { QK } from "../../../../../../base/qk";
import CustomInput from "../../../../../FormFields/CustomInput";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: 540 },
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
  p: 3.5,
};

const reasonOptions = [
  { value: "Damaged", label: "Damaged / Broken" },
  { value: "Expired", label: "Expired" },
  { value: "Over Dispensed", label: "Over Dispensed" },
  { value: "Lost", label: "Lost / Missing" },
  { value: "Returned", label: "Returned to Pharmacy" },
  { value: "Correction", label: "Manual Correction" },
  { value: "Other", label: "Other" },
];

const AdjustStockModal = ({
  open,
  handleClose,
  medication,
  clientID,
}) => {
  const [direction, setDirection] = useState("remove");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [medicineName, setMedicineName] = useState("");

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const isIndividual = medication?.medicine_type === "Individual Medication";
  const isBlister = medication?.medicine_type === "Blister Pack";

  const currentStock = isBlister
    ? Number(medication?.total_slots_remaining || 0)
    : null;

  const individualMeds = medication?.individual_meds || [];
  const selectedMedStock = isIndividual
    ? Number(
        individualMeds.find((m) => m.name === medicineName)?.remaining_stock || 0
      )
    : null;

  useEffect(() => {
    if (!open || !medication) {
      setDirection("remove");
      setQuantity("");
      setReason("");
      setNote("");
      setMedicineName("");
      return;
    }

    if (medication.medicine_type === "Individual Medication") {
      const first = medication.individual_meds?.[0]?.name || "";
      setMedicineName(first);
    }
  }, [open, medication]);

  const currentBase = isBlister ? currentStock : selectedMedStock;

  const adjustmentValue =
    direction === "add"
      ? Math.abs(Number(quantity) || 0)
      : -Math.abs(Number(quantity) || 0);

  const newBalance = Math.max(0, (currentBase || 0) + adjustmentValue);

  const { mutate, isLoading } = useMutation(
    (payload) => adjustMedicationStock(medication.id, payload),
    {
      onSuccess: () => {
        enqueueSnackbar("Stock adjusted successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.clientMedications, clientID]);
        queryClient.invalidateQueries([QK.medicationDetails, medication.id]);
        queryClient.invalidateQueries([
          QK.medicationrefillhistory,
          medication.id,
        ]);
        handleModalClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.detail || "Failed to adjust stock",
          { variant: "error" }
        );
      },
    }
  );

  const handleSubmit = () => {
    if (!quantity || Number(quantity) <= 0) {
      enqueueSnackbar("Please enter a valid quantity", { variant: "warning" });
      return;
    }
    if (!reason) {
      enqueueSnackbar("Please select a reason", { variant: "warning" });
      return;
    }
    if (isIndividual && !medicineName) {
      enqueueSnackbar("Please select a medicine", { variant: "warning" });
      return;
    }

    const payload = {
      adjustment: adjustmentValue,
      reason,
      note: note || "",
    };

    if (isIndividual) {
      payload.medicine_name = medicineName;
    }

    mutate(payload);
  };

  const handleModalClose = () => {
    setDirection("remove");
    setQuantity("");
    setReason("");
    setNote("");
    setMedicineName("");
    handleClose();
  };

  if (!medication) return null;

  const isNegativeWarning =
    direction === "remove" && Number(quantity) > (currentBase || 0);

  return (
    <Modal
      open={open}
      onClose={() => !isLoading && handleModalClose()}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.6)" } }}
    >
      <Box sx={style}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                bgcolor: alpha("#F59E0B", 0.1),
                color: "#F59E0B",
                p: 1,
                borderRadius: 2,
                display: "flex",
              }}
            >
              <Inventory sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Adjust Stock
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                {medication.title || medication.name}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleModalClose}
            size="small"
            disabled={isLoading}
            sx={{ bgcolor: "#f1f5f9" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={2.5}>
          {isIndividual && individualMeds.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Medicine</InputLabel>
              <Select
                value={medicineName}
                label="Medicine"
                onChange={(e) => setMedicineName(e.target.value)}
                disabled={isLoading}
              >
                {individualMeds.map((m, idx) => (
                  <MenuItem key={idx} value={m.name}>
                    {m.name} — Stock:{" "}
                    {Number(m.remaining_stock ?? m.initial_stock_qty ?? 0)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#64748b",
                mb: 1,
              }}
            >
              Adjustment Type
            </Typography>
            <ToggleButtonGroup
              value={direction}
              exclusive
              onChange={(e, val) => val && setDirection(val)}
              fullWidth
              size="small"
              disabled={isLoading}
            >
              <ToggleButton
                value="add"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected": {
                    bgcolor: alpha("#10B981", 0.15),
                    color: "#047857",
                    "&:hover": { bgcolor: alpha("#10B981", 0.25) },
                  },
                }}
              >
                <AddIcon sx={{ mr: 0.5, fontSize: 18 }} /> Add
              </ToggleButton>
              <ToggleButton
                value="remove"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected": {
                    bgcolor: alpha("#EF4444", 0.15),
                    color: "#B91C1C",
                    "&:hover": { bgcolor: alpha("#EF4444", 0.25) },
                  },
                }}
              >
                <RemoveIcon sx={{ mr: 0.5, fontSize: 18 }} /> Remove
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              bgcolor: "#f8fafc",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 600 }}
              >
                CURRENT STOCK
              </Typography>
              <Typography sx={{ fontSize: "1.2rem", fontWeight: 700 }}>
                {currentBase ?? 0}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomInput
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={isLoading}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 600 }}
              >
                NEW BALANCE
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: isNegativeWarning ? "#EF4444" : "#10B981",
                }}
              >
                {newBalance}
              </Typography>
            </Box>
          </Box>

          {isNegativeWarning && (
            <Chip
              icon={<WarningAmber sx={{ fontSize: 16 }} />}
              label="Removing more than available. Stock will be 0."
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Reason</InputLabel>
            <Select
              value={reason}
              label="Reason"
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
            >
              {reasonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Note (optional)"
            multiline
            rows={2}
            size="small"
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isLoading}
            placeholder="e.g., 3 slots torn during handling"
          />
        </Stack>

        <Box sx={{ mt: 3.5, display: "flex", gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleModalClose}
            disabled={isLoading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#e2e8f0",
              color: "#64748b",
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={
              isLoading || !quantity || Number(quantity) <= 0 || !reason
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#F59E0B",
              "&:hover": { bgcolor: "#D97706" },
            }}
          >
            {isLoading ? "Adjusting..." : "Confirm Adjustment"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AdjustStockModal;